import { createPortal } from "react-dom";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { MessageSquare } from "lucide-react";

import {
  BatchTeamNotesPanel,
  batchTeamNotesHoverHint,
  type BatchTeamNotesPreview,
} from "@/components/manufacturing/BatchTeamNotesPanel";
import { HoverHintTrigger } from "@/components/ui/title-help";
import { cn } from "@/lib/utils";
import {
  computePointAnchoredContextMenu,
  FLOATING_POPOVER_Z_INDEX,
  type AnchoredMenuPanelBox,
} from "@/lib/viewportAnchoredPopover";

export interface BatchTeamNotesGlyphProps {
  batchId: number;
  teamNoteCount: number;
  latestTeamNote: BatchTeamNotesPreview | null;
  hubWritesAllowed: boolean;
}

export function BatchTeamNotesGlyph({
  batchId,
  teamNoteCount,
  latestTeamNote,
  hubWritesAllowed,
}: BatchTeamNotesGlyphProps): JSX.Element {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ clientX: number; clientY: number } | null>(null);
  const [menuBox, setMenuBox] = useState<AnchoredMenuPanelBox | null>(null);

  const hasNotes = teamNoteCount > 0;

  const repositionMenu = useCallback(() => {
    if (!anchor) {
      setMenuBox(null);
      return;
    }
    setMenuBox(
      computePointAnchoredContextMenu({
        clientX: anchor.clientX,
        clientY: anchor.clientY,
        menuWidth: 20 * 16,
        maxHeightCap: Math.min(720, Math.floor(window.innerHeight * 0.9)),
      }),
    );
  }, [anchor]);

  useLayoutEffect(() => {
    if (!editorOpen || anchor == null) {
      setMenuBox(null);
      return;
    }
    repositionMenu();
    const onLayout = (): void => {
      repositionMenu();
    };
    window.addEventListener("resize", onLayout);
    window.addEventListener("scroll", onLayout, true);
    window.visualViewport?.addEventListener("resize", onLayout);
    window.visualViewport?.addEventListener("scroll", onLayout);
    return () => {
      window.removeEventListener("resize", onLayout);
      window.removeEventListener("scroll", onLayout, true);
      window.visualViewport?.removeEventListener("resize", onLayout);
      window.visualViewport?.removeEventListener("scroll", onLayout);
    };
  }, [editorOpen, anchor, repositionMenu]);

  useEffect(() => {
    if (!editorOpen) return;
    const onMouseDown = (event: MouseEvent): void => {
      if (menuRef.current?.contains(event.target as Node)) return;
      setEditorOpen(false);
      setAnchor(null);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        setEditorOpen(false);
        setAnchor(null);
      }
    };
    const timer = window.setTimeout(() => {
      document.addEventListener("mousedown", onMouseDown);
    }, 0);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [editorOpen]);

  return (
    <>
      <HoverHintTrigger
        align="start"
        trigger={
          <button
            type="button"
            className={cn(
              "inline-flex h-6 w-6 items-center justify-center rounded border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400",
              hasNotes && "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100",
            )}
            aria-label={hasNotes ? "Team notes" : "Add team notes"}
            onClick={(event) => {
              event.stopPropagation();
              setAnchor({ clientX: event.clientX, clientY: event.clientY });
              setEditorOpen(true);
            }}
          >
            <MessageSquare className="h-3.5 w-3.5" aria-hidden />
          </button>
        }
      >
        {batchTeamNotesHoverHint(teamNoteCount, latestTeamNote)}
      </HoverHintTrigger>

      {editorOpen && menuBox != null && anchor != null
        ? createPortal(
            <div
              ref={menuRef}
              role="dialog"
              aria-label={`Batch ${String(batchId)} team notes`}
              className="fixed flex min-h-0 flex-col overflow-hidden rounded-md border border-slate-200 bg-white p-3 shadow-lg"
              style={{
                zIndex: FLOATING_POPOVER_Z_INDEX,
                top: menuBox.top,
                left: menuBox.left,
                width: menuBox.width,
                maxHeight: menuBox.maxHeight,
              }}
            >
              <p className="mb-2 shrink-0 text-sm font-semibold text-slate-900">
                Batch {String(batchId)} team notes
              </p>
              <BatchTeamNotesPanel
                batchId={batchId}
                hubWritesAllowed={hubWritesAllowed}
                variant="compact"
              />
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
